/* eslint-disable complexity */
/* eslint-disable max-lines */
import { BenefitTypeOptions, ReferentPriceTypes } from '@strata/constants';
import { calcBidPercentage, sortAsc } from '@strata/helper-functions';
import { Checkbox } from '@strata/react-checkbox-list';
import { DateInputFormik } from '@strata/react-date-time';
import { FormContainer } from '@strata/react-form-container';
import { BidInput, TextInputFormik } from '@strata/react-inputs';
import { RadioButtonGroupFormik } from '@strata/react-radio-button-group';
import { SuggestSingleSelectFormik } from '@strata/react-selects';
import { ErrorToast, SuccessToast } from '@strata/react-toast-message';
import { validationStrings } from '@strata/strata-ui-strings';
import Decimal from 'decimal.js';
import { Formik } from 'formik';
import { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router';
import * as Yup from 'yup';

import { ListPageHeader } from 'components/ListPageHeader';
import { CREATE_ROUTE } from 'services/constants';
import { handleError } from 'services/ErrorManagement';
import { post, put, useGet } from 'services/Http';
import { getPayerSegmentMap, mapPayerSegment } from 'services/PayerSegmentSupport';

const sortNameAsc = (a, b) => sortAsc(a.name, b.name);

export const RateRuleDetails = () => {
    const { rateRuleId } = useParams();
    const navigate = useNavigate();

    const goHome = () => navigate('/rate-rules');

    const isCreate = rateRuleId === CREATE_ROUTE;

    const rateRule = useGet(!isCreate ? `/ratesetter/admin/rate_rules/${rateRuleId}` : null);

    const payers = useGet('/reference/payers/');
    const payerOptions = useMemo(
        () =>
            payers
                ?.map((payer) => ({
                    name: payer.payer_name,
                    value: payer.payer_id,
                    secondaryData: payer.payer_id,
                }))
                .sort(sortNameAsc),
        [payers],
    );

    const payerSegmentsList = useGet('/reference/payer_segments');
    const payerSegmentOptions = useMemo(
        () => payerSegmentsList?.map(mapPayerSegment).sort(sortNameAsc),
        [payerSegmentsList],
    );
    const payerSegmentMap = getPayerSegmentMap(payerSegmentsList);

    const products = useGet('/reference/productservices/summaryonly');
    const productOptions = useMemo(
        () =>
            products
                ?.map((product) => ({
                    name: product.description,
                    value: product.oid,
                    secondaryData: product.oid,
                }))
                .sort(sortNameAsc),
        [products],
    );

    const placesOfService = useGet('/reference/product_benefit_configs/places');
    const placeOfServiceOptions = useMemo(
        () =>
            placesOfService
                ? [
                      { name: 'All', value: null },
                      ...placesOfService.map((place) => ({
                          name: place.description,
                          value: place.code,
                      })),
                  ]
                : undefined,
        [placesOfService],
    );

    const priceSources = useGet('/reference/prices/sources');
    const priceSourceOptions = (priceSources ?? []).map((source) => ({
        text: source.display_name,
        value: source.price_source,
    }));

    const marketOverrideReasons = useGet(
        '/ratesetter/admin/rate_rules/market_override_reason/values',
    );

    const lineOfBusinessOptions = [
        { name: 'Medicare', value: 'Medicare' },
        { name: 'Commercial', value: 'Commercial' },
        { name: 'Commercial ACA', value: 'Commercial ACA' },
        { name: 'All', value: null },
    ];

    return (
        <Formik
            initialValues={{
                payer: payerOptions?.find(({ value }) => value === rateRule?.payer_id),
                product: productOptions?.find(
                    ({ value }) => value === rateRule?.product_service_oid,
                ),
                is_generic: rateRule?.is_generic,
                benefit: rateRule?.benefit,
                placeOfService: placeOfServiceOptions?.find(
                    ({ value }) => value === (rateRule?.place_of_service ?? null),
                ),
                line_of_business: rateRule?.line_of_business ?? null,
                location: rateRule?.location ?? undefined,
                segment:
                    rateRule?.segment_id &&
                    mapPayerSegment(payerSegmentMap?.[rateRule?.segment_id]),
                referent_price_type: rateRule?.referent_price_type ?? ReferentPriceTypes.ASP,
                price_source: rateRule?.price_source,
                is_frozen: rateRule?.is_frozen ?? false,
                default_rate: calcBidPercentage(rateRule?.default_rate),
                baseline_rate: calcBidPercentage(rateRule?.baseline_rate),
                pbm_rate: calcBidPercentage(rateRule?.pbm_rate ?? undefined),
                market_override_rate: calcBidPercentage(
                    rateRule?.market_override_rate ?? undefined,
                ),
                market_override_reason: rateRule?.market_override_reason ?? undefined,
                effective_start_date: rateRule?.effective_start_date,
            }}
            enableReinitialize
            validationSchema={Yup.object({
                payer: Yup.object().required(validationStrings.requiredField.errorMessage),
                product: Yup.object().required(validationStrings.requiredField.errorMessage),
                is_generic: Yup.bool().required(validationStrings.requiredField.errorMessage),
                benefit: Yup.string().required(validationStrings.requiredField.errorMessage),
                placeOfService: Yup.object().required(validationStrings.requiredField.errorMessage),
                line_of_business: Yup.string().nullable(),
                location: Yup.string(),
                segment: Yup.object().nullable(),
                referent_price_type: Yup.string().required(
                    validationStrings.requiredField.errorMessage,
                ),
                price_source: Yup.string().required(validationStrings.requiredField.errorMessage),
                is_frozen: Yup.bool().required(validationStrings.requiredField.errorMessage),
                default_rate: BidInput.validator().required(
                    validationStrings.requiredField.errorMessage,
                ),
                baseline_rate: BidInput.validator().required(
                    validationStrings.requiredField.errorMessage,
                ),
                pbm_rate: BidInput.validator(),
                market_override_rate: BidInput.validator(),
                market_override_reason: Yup.string().when('market_override_rate', {
                    is: (value) => value != null && value !== '',
                    then: (schema) => schema.required(validationStrings.requiredField.errorMessage),
                }),
                effective_start_date: DateInputFormik.validator().required(
                    validationStrings.requiredField.errorMessage,
                ),
            })}
            onSubmit={async (values) => {
                try {
                    const body = {
                        payer_id: values.payer.value,
                        rate_rules: [
                            {
                                product_service_oid: values.product.value,
                                is_generic: values.is_generic,
                                benefit: values.benefit,
                                place_of_service: values.placeOfService.value,
                                line_of_business: values.line_of_business || undefined,
                                location: values.location || undefined,
                                segment_id: values.segment?.segment_id || undefined,
                                referent_price_type: values.referent_price_type,
                                price_source: values.price_source,
                                is_frozen: values.is_frozen,
                                default_rate: Decimal.div(values.default_rate, 100).toNumber(),
                                baseline_rate: Decimal.div(values.baseline_rate, 100).toNumber(),
                                pbm_rate:
                                    values.pbm_rate != null && values.pbm_rate !== ''
                                        ? Decimal.div(values.pbm_rate, 100).toNumber()
                                        : undefined,
                                market_override_rate:
                                    values.market_override_rate != null &&
                                    values.market_override_rate !== ''
                                        ? Decimal.div(values.market_override_rate, 100).toNumber()
                                        : undefined,
                                market_override_reason: values.market_override_reason || undefined,
                                effective_start_date: values.effective_start_date,
                            },
                        ],
                    };

                    const result = await (isCreate ? post : put)(
                        '/ratesetter/admin/rate_rules/',
                        body,
                    );

                    if (result?.issues?.length > 0) {
                        ErrorToast({ message: result.issues[0] });
                    } else {
                        SuccessToast({
                            message: `Rate rule ${isCreate ? 'added' : 'updated'} successfully.`,
                        });
                        goHome();
                    }
                } catch (err) {
                    handleError('Error saving rate rule.', err);
                }
            }}
        >
            {(formik) => (
                <FormContainer
                    formTitle="Rate Rule Details"
                    isLoading={!(isCreate || rateRule) || !products || !payers}
                    onFormSubmit={formik.handleSubmit}
                    isSubmitting={formik.isSubmitting || !(formik.dirty && formik.isValid)}
                    onSecondaryButtonClick={goHome}
                    formLeftContent={
                        <>
                            <ListPageHeader pageTitle="Rate Rule Definition" />
                            <SuggestSingleSelectFormik
                                id="payer"
                                label="Payer"
                                required
                                disabled={!isCreate}
                                values={payerOptions}
                            />
                            <SuggestSingleSelectFormik
                                id="product"
                                label="Product"
                                required
                                disabled={!isCreate}
                                values={productOptions}
                            />
                            <RadioButtonGroupFormik
                                name="is_generic"
                                label="Product Type"
                                required
                                disabled={!isCreate}
                                options={[
                                    { value: false, text: 'Brand' },
                                    { value: true, text: 'Generic' },
                                ]}
                                onChange={(newVal) =>
                                    formik.setFieldValue('is_generic', newVal === 'true')
                                }
                            />
                            <RadioButtonGroupFormik
                                name="benefit"
                                label="Benefit"
                                required
                                disabled={!isCreate}
                                options={BenefitTypeOptions}
                            />
                            <SuggestSingleSelectFormik
                                id="placeOfService"
                                label="Place Of Service"
                                required
                                disabled={!isCreate}
                                values={placeOfServiceOptions}
                                isLoading={!placeOfServiceOptions}
                            />
                            <SuggestSingleSelectFormik
                                id="line_of_business"
                                label="Line Of Business"
                                required
                                disabled={!isCreate}
                                values={lineOfBusinessOptions}
                                onChange={(newValue) =>
                                    formik.setFieldValue('line_of_business', newValue.value)
                                }
                                selectedItem={lineOfBusinessOptions.find(
                                    ({ value }) => value === formik.values.line_of_business,
                                )}
                            />
                            <TextInputFormik id="location" label="Location" disabled={!isCreate} />
                            <SuggestSingleSelectFormik
                                id="segment"
                                label="Payer Segment"
                                disabled={!isCreate}
                                values={payerSegmentOptions?.filter((segment) => {
                                    const payerId = formik.values?.payer?.value;
                                    if (payerId) {
                                        return payerId === segment?.payer_id;
                                    }
                                    return true;
                                })}
                            />
                            <ListPageHeader pageTitle="Rate Rule Configuration" />
                            <RadioButtonGroupFormik
                                name="referent_price_type"
                                label="Referent"
                                required
                                options={[ReferentPriceTypes.ASP, ReferentPriceTypes.AWP]}
                                onChange={(newValue) =>
                                    formik.setValues({
                                        ...formik.values,
                                        referent_price_type: newValue,
                                        default_rate: undefined,
                                        baseline_rate: undefined,
                                        pbm_rate: undefined,
                                        market_override_rate: undefined,
                                        market_override_reason: undefined,
                                    })
                                }
                            />
                            <RadioButtonGroupFormik
                                name="price_source"
                                label="Price List Source"
                                required
                                options={priceSourceOptions}
                            />
                            <Checkbox
                                name="is_frozen"
                                label="Lock unit price for the life of assigned referrals."
                                checked={formik.values.is_frozen}
                                onChange={(checked) => formik.setFieldValue('is_frozen', checked)}
                                onBlur={formik.handleBlur}
                            />
                            <BidInput
                                id="default_rate"
                                label="Default Rate"
                                referentPriceType={formik.values.referent_price_type}
                            />
                            <BidInput
                                id="baseline_rate"
                                label="Baseline Rate"
                                referentPriceType={formik.values.referent_price_type}
                            />
                            <BidInput
                                id="pbm_rate"
                                label="PBM Rate"
                                required={false}
                                referentPriceType={formik.values.referent_price_type}
                            />
                            <BidInput
                                id="market_override_rate"
                                label="Market Override Rate"
                                required={false}
                                referentPriceType={formik.values.referent_price_type}
                            />
                            <SuggestSingleSelectFormik
                                id="market_override_reason"
                                label="Market Override Reason"
                                values={marketOverrideReasons}
                            />
                            <DateInputFormik
                                id="effective_start_date"
                                label="Effective Start Date And Time"
                                required
                                showTime
                            />
                        </>
                    }
                />
            )}
        </Formik>
    );
};
